import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import iro from '@jaames/iro';
import { SelectedProjectListControllerComponent } from '../selected-project-list-controller/selected-project-list-controller.component';
import { Subject, take, takeUntil } from 'rxjs';
import { EmbedData } from '../../data/embed';
import { EmbedService } from '../../../../embed-layout/common/services/embed.service';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';

@Component({
  selector: 'color-setting-tool',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    SelectedProjectListControllerComponent,
  ],
  templateUrl: './color-setting-tool.component.html',
  styleUrl: './color-setting-tool.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorSettingToolComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mainDom') mainDom: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('boxColorPicker') boxColorPicker: ElementRef<HTMLDivElement> | undefined;

  public isColorChange: boolean = false;

  public red: string | undefined;
  public green: string | undefined;
  public blue: string | undefined;
  public color: string = '#ffffff';

  public width: number = 150;

  private boxColorPickerIro: iro.ColorPicker | undefined;
  public embedData: EmbedData | undefined;

  public selectedProjectIndex: number = 0;

  private unsubscribeAll: Subject<any> = new Subject();
  constructor(
    private threeGraphqlService: ThreeGraphqlService,
    private embedService: EmbedService,
    private cd: ChangeDetectorRef,
  ) {}

  //--------------------------------------------------------------------------------------------------------
  // @ Life Circle
  //--------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.getEmbedData();
    this.getSelectedProjectIndex();
  }

  ngAfterViewInit(): void {
    this.initColorPicker();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
    this.unsubscribeAll.unsubscribe();
  }

  //--------------------------------------------------------------------------------------------------------
  // @ Public Method
  //--------------------------------------------------------------------------------------------------------

  public onClicColor(color: string): void {
    if (!this.boxColorPicker) return;

    this.boxColorPickerIro?.color.set(color);
  }

  public onChangeIsColor(): void {
    if (!this.embedData) return;
    if (!this.embedData.projects[this.selectedProjectIndex].setting)
      this.embedData.projects[this.selectedProjectIndex].setting = {};
    this.embedData.projects[this.selectedProjectIndex].setting['isColor'] = this.isColorChange;
    this.embedData.projects[this.selectedProjectIndex].setting['colors'] = this.embedData.projects[
      this.selectedProjectIndex
    ].colors
      ? [...this.embedData.projects[this.selectedProjectIndex].colors]
      : [];
    this.threeGraphqlService
      .updateEmbedProject(
        this.embedData.projects[this.selectedProjectIndex].uuid,
        this.embedData.projects[this.selectedProjectIndex].setting,
      )
      .pipe(take(1))
      .subscribe((embedProject: any) => {
        if (!embedProject) return;
        if (!this.embedData) return;
        if (!this.embedData.projects) return;
        if (!this.embedData.projects[this.selectedProjectIndex]) return;
        this.embedData.projects[this.selectedProjectIndex].colors = embedProject.colors;
        this.embedService.embedData = this.embedData;
        this.cd.markForCheck();
      });
  }

  public onClickColorAddBtn(): void {
    if (!this.embedData) return;
    if (!this.embedData.projects) return;
    if (!this.embedData.projects[this.selectedProjectIndex]) return;
    const project = this.embedData.projects[this.selectedProjectIndex];
    if (!project.colors) project.colors = [];
    project.colors.push(this.color);
    if (!project.setting) project.setting = {};
    project.setting['colors'] = [...project.colors];
    this.threeGraphqlService
      .updateEmbedProject(project.uuid, project.setting)
      .pipe(take(1))
      .subscribe((embedProject: any) => {
        if (!embedProject) return;
        if (!this.embedData) return;
        if (!this.embedData.projects) return;
        if (!this.embedData.projects[this.selectedProjectIndex]) return;
        this.embedData.projects[this.selectedProjectIndex].colors = embedProject.colors;
        this.embedService.embedData = this.embedData;
        this.cd.markForCheck();
      });
  }

  public deleteColor(color: string): void {
    if (!this.embedData) return;
    if (!this.embedData.projects[this.selectedProjectIndex].colors) return;
    this.embedData.projects[this.selectedProjectIndex].colors = this.embedData.projects[
      this.selectedProjectIndex
    ].colors.filter((item: string) => item !== color);
    if (!this.embedData.projects[this.selectedProjectIndex].setting)
      this.embedData.projects[this.selectedProjectIndex].setting = {};
    this.embedData.projects[this.selectedProjectIndex].setting['colors'] = [
      ...this.embedData.projects[this.selectedProjectIndex].colors,
    ];
    this.threeGraphqlService
      .updateEmbedProject(
        this.embedData.projects[this.selectedProjectIndex].uuid,
        this.embedData.projects[this.selectedProjectIndex].setting,
      )
      .pipe(take(1))
      .subscribe((embedProject: any) => {
        if (!embedProject) return;
        if (!this.embedData) return;
        if (!this.embedData.projects) return;
        if (!this.embedData.projects[this.selectedProjectIndex]) return;
        this.embedData.projects[this.selectedProjectIndex].colors = embedProject.colors;
        this.embedService.embedData = this.embedData;
        this.cd.markForCheck();
      });
  }

  //--------------------------------------------------------------------------------------------------------
  // @ Private Method
  //--------------------------------------------------------------------------------------------------------

  private initColorPicker(): void {
    this.initBoxColorPicker();
  }
  private initBoxColorPicker(): void {
    const mainLayoutOptions: any = [
      {
        component: iro.ui.Box,
      },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'hue',
          sliderSize: 10,
        },
      },
    ];
    if (!this.boxColorPicker?.nativeElement) return;
    this.width = (this.mainDom?.nativeElement.clientWidth || 150) - (this.width > 300 ? 50 : 30);
    this.boxColorPickerIro = iro.ColorPicker(this.boxColorPicker.nativeElement, {
      width: this.width > 300 ? this.width / 2.25 : this.width,
      height: 50,
      color: this.color || '#ffffff',
      borderWidth: 0,
      borderColor: '#fff',
      layoutDirection: 'horizontal',
      margin: 10,
      layout: mainLayoutOptions,
    });
    this.getBoxColorPickerColorEvent();
  }

  private getBoxColorPickerColorEvent(): void {
    if (!this.boxColorPickerIro) return;
    this.boxColorPickerIro.on(['color:change'], (color: iro.Color) => {
      this.color = color.hexString;
      this.red = color.red.toString();
      this.green = color.green.toString();
      this.blue = color.blue.toString();
      this.cd.markForCheck();
    });
  }

  private getEmbedData(): void {
    this.embedService.embedData$.pipe(takeUntil(this.unsubscribeAll)).subscribe((embedData) => {
      if (!embedData) return;
      console.log(embedData);
      this.embedData = embedData;
      this.cd.markForCheck();
    });
  }

  private getSelectedProjectIndex(): void {
    this.embedService.selectedProjectIndex$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((index) => {
        this.selectedProjectIndex = index;

        (this.isColorChange =
          this.embedData?.projects[this.selectedProjectIndex]?.setting?.isColor || false),
          this.cd.markForCheck();
      });
  }
}
